import { useMemo } from 'react';

import { ComboboxField, type ComboboxItems, type ComboboxOption } from '@/_shared/components/ui/combobox';

import { getLocalTimezone, getSuggestedValues, getTimezoneOptions } from '../lib/timezones';

/**
 * Timezone picker built on the shared `ComboboxField` (popup variant: button trigger + in-popover
 * search). Pins a "Suggested" group (local + UTC) above the full list; Base UI hides the group while
 * filtering, so a search shows only real matches.
 */
export function TimezoneCombobox({
  value,
  onChange,
  disabled = false,
  id,
}: {
  value: string;
  onChange: (tz: string) => void;
  disabled?: boolean;
  /** DOM id on the trigger so an external `<Label htmlFor>` associates with the field. */
  id?: string;
}): React.ReactElement {
  const options = useMemo<ComboboxItems>(() => {
    // `keywords` carries the IANA id / region so a search matches more than the display label
    // (e.g. typing "America/New_York" or "asia" finds the zone, not just the "New York (UTC…)" label).
    const all: ComboboxOption[] = getTimezoneOptions().map((o) => ({
      value: o.value,
      label: o.label,
      keywords: o.search,
    }));
    // Keep the current value selectable even if it's an alias absent from the curated list
    // (e.g. a browser-reported "Asia/Calcutta" vs the canonical "Asia/Kolkata").
    if (value && !all.some((o) => o.value === value)) all.unshift({ value, label: value });

    const suggestedValues = new Set(getSuggestedValues(getLocalTimezone()));
    const suggested = all.filter((o) => suggestedValues.has(o.value));
    const rest = all.filter((o) => !suggestedValues.has(o.value));

    return suggested.length > 0
      ? [
          { title: 'Suggested', options: suggested },
          { title: 'All timezones', options: rest },
        ]
      : all;
  }, [value]);

  return (
    <ComboboxField
      mode="single"
      variant="popup"
      id={id}
      options={options}
      value={value}
      onValueChange={onChange}
      disabled={disabled}
      placeholder="Timezone"
      aria-label="Timezone"
      emptyMessage="No timezones found"
      className="w-full"
    />
  );
}
